/**
 * Canonical duration formatter for Seamigo.
 *
 * Rules (matching user expectation):
 *   < 30 days  → days only            "15d"  / "15 days"
 *   30–364 days → months + days        "4m 15d"  / "4 months 15 days"
 *   365+ days  → years + months + days "10y 2m 10d"  / "10 years 2 months 10 days"
 *
 * All non-zero parts are always shown when the magnitude warrants it.
 */

function decompose(totalDays: number): { years: number; months: number; days: number } {
  const abs = Math.abs(Math.round(totalDays));
  const years = Math.floor(abs / 365);
  const rem = abs % 365;
  const months = Math.floor(rem / 30);
  const days = rem % 30;
  return { years, months, days };
}

/**
 * Short form: "10y 2m 10d"  — for compact UI elements (badges, table cells, tooltips).
 * Handles negative values (e.g. cert already expired): returns "-15d", "-4m 15d" etc.
 */
export function formatDaysShort(totalDays: number): string {
  if (totalDays === 0) return '0d';
  const sign = totalDays < 0 ? '-' : '';
  const abs = Math.abs(Math.round(totalDays));
  const { years, months, days } = decompose(abs);

  if (abs < 30) return `${sign}${abs}d`;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0) parts.push(`${days}d`);
  return sign + (parts.length > 0 ? parts.join(' ') : '0d');
}

/**
 * Long form: "10 years 2 months 10 days" — for profile stats, CV templates, readable text.
 * Returns "Not recorded" for zero / negative input (sea service context).
 */
export function formatDaysLong(totalDays: number): string {
  if (totalDays <= 0) return 'Not recorded';
  const { years, months, days } = decompose(totalDays);

  const pl = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

  if (totalDays < 30) return pl(Math.round(totalDays), 'day');

  const parts: string[] = [];
  if (years > 0) parts.push(pl(years, 'year'));
  if (months > 0) parts.push(pl(months, 'month'));
  if (days > 0) parts.push(pl(days, 'day'));
  return parts.length > 0 ? parts.join(' ') : '0 days';
}
